import {
  WHATSHOT_ACCEPTED_IMAGE_TYPES,
  WHATSHOT_MAX_BATCH_BYTES,
  WHATSHOT_MAX_BLOB_BYTES,
  WHATSHOT_MAX_FRAMES_PER_PRODUCT,
  WHATSHOT_MAX_PRODUCTS,
  type WhatshotFrameGroup,
  type WhatshotTranscript,
  type WhatshotTranscriptSegment,
} from '@/utils/indexedWhatshot'

export const WHATSHOT_HANDOFF_ROUTE_PATH = '/whatshot-handoff'
export const WHATSHOT_HANDOFF_PROTOCOL = 'whatshot-handoff/v1'
export const WHATSHOT_HANDOFF_READY = 'WHATSHOT_HANDOFF_READY'
export const WHATSHOT_HANDOFF_PAYLOAD = 'WHATSHOT_HANDOFF_PAYLOAD'
export const WHATSHOT_HANDOFF_STORED = 'WHATSHOT_HANDOFF_STORED'
export const WHATSHOT_HANDOFF_ERROR = 'WHATSHOT_HANDOFF_ERROR'

export const WHATSHOT_HANDOFF_TIMEOUT_MS = 30_000
export const WHATSHOT_SENDER_ORIGIN_QUERY_PARAM = 'senderOrigin'
const WHATSHOT_MAX_TRANSCRIPT_SEGMENTS = 10_000
const WHATSHOT_MAX_TRANSCRIPT_TEXT_LENGTH = 1_000
const WHATSHOT_MAX_TRANSCRIPT_TOTAL_CHARS = 1_000_000

const DEFAULT_WHATSHOT_ORIGINS = new Set([
  'https://whatshot.live',
  'https://whatshot.ai',
  'https://bazaart.github.io',
])

const LOOPBACK_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]'])

export interface WhatshotHandoffPayload {
  requestId: string
  transferId: string
  groups: WhatshotFrameGroup[]
  totalProductCount: number
  transcript?: WhatshotTranscript
}

type ValidatePayloadResult =
  | { ok: true; payload: WhatshotHandoffPayload }
  | { ok: false; error: string; requestId?: string }

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function configuredWhatshotOrigin(): string | null {
  const configured = process.env.REACT_APP_WHATSHOT_ORIGIN?.trim()
  if (!configured) return null
  try {
    return new URL(configured).origin
  } catch {
    return null
  }
}

function allowsLocalWhatshotOrigins(): boolean {
  return process.env.NODE_ENV === 'development'
}

function isLoopbackWhatshotOrigin(url: URL): boolean {
  return (
    (url.protocol === 'http:' || url.protocol === 'https:') &&
    LOOPBACK_HOSTNAMES.has(url.hostname)
  )
}

function isAllowedBlob(value: unknown): value is Blob {
  if (!isRecord(value)) return false
  const blob = value as { size?: unknown; type?: unknown; arrayBuffer?: unknown }
  return (
    typeof blob.size === 'number' &&
    blob.size > 0 &&
    blob.size <= WHATSHOT_MAX_BLOB_BYTES &&
    typeof blob.type === 'string' &&
    WHATSHOT_ACCEPTED_IMAGE_TYPES.includes(
      blob.type as (typeof WHATSHOT_ACCEPTED_IMAGE_TYPES)[number],
    ) &&
    typeof blob.arrayBuffer === 'function'
  )
}

function validateTimestamp(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null
}

function validateTranscript(value: unknown): WhatshotTranscript | null {
  if (!isRecord(value) || value.language !== 'en' || !Array.isArray(value.segments)) return null
  if (value.segments.length > WHATSHOT_MAX_TRANSCRIPT_SEGMENTS) return null

  let totalChars = 0
  const segments: WhatshotTranscriptSegment[] = []
  for (const valueSegment of value.segments) {
    if (!isRecord(valueSegment)) return null
    const start = validateTimestamp(valueSegment.start)
    const end = validateTimestamp(valueSegment.end)
    const text = typeof valueSegment.text === 'string' ? valueSegment.text.trim() : ''
    if (
      start === null ||
      end === null ||
      end < start ||
      !text ||
      text.length > WHATSHOT_MAX_TRANSCRIPT_TEXT_LENGTH
    ) {
      return null
    }
    totalChars += text.length
    if (totalChars > WHATSHOT_MAX_TRANSCRIPT_TOTAL_CHARS) return null
    segments.push({ start, end, text })
  }
  return { language: 'en', segments }
}

export function isTrustedWhatshotOrigin(origin: string): boolean {
  try {
    const url = new URL(origin)
    if (allowsLocalWhatshotOrigins() && isLoopbackWhatshotOrigin(url)) {
      return true
    }
    const normalizedOrigin = url.origin
    const configuredOrigin = configuredWhatshotOrigin()
    return configuredOrigin
      ? normalizedOrigin === configuredOrigin
      : DEFAULT_WHATSHOT_ORIGINS.has(normalizedOrigin)
  } catch {
    return false
  }
}

export function getWhatshotSenderOriginFromSearch(search: string): string | null {
  const value = new URLSearchParams(search).get(WHATSHOT_SENDER_ORIGIN_QUERY_PARAM)
  if (!value) return null
  try {
    const origin = new URL(value).origin
    return isTrustedWhatshotOrigin(origin) ? origin : null
  } catch {
    return null
  }
}

export function isTrustedWhatshotMessageEvent(
  event: Pick<MessageEvent<unknown>, 'origin' | 'source'>,
  senderWindow: WindowProxy,
  senderOrigin: string,
): boolean {
  return (
    event.source === senderWindow &&
    event.origin === senderOrigin &&
    isTrustedWhatshotOrigin(event.origin)
  )
}

export function createWhatshotReadyMessage() {
  return {
    type: WHATSHOT_HANDOFF_READY,
    protocol: WHATSHOT_HANDOFF_PROTOCOL,
  } as const
}

export function createWhatshotStoredMessage(payload: {
  requestId: string
  transferId: string
}) {
  return {
    type: WHATSHOT_HANDOFF_STORED,
    protocol: WHATSHOT_HANDOFF_PROTOCOL,
    requestId: payload.requestId,
    transferId: payload.transferId,
  } as const
}

export function createWhatshotErrorMessage(payload: {
  code: string
  message: string
  requestId?: string
}) {
  return {
    type: WHATSHOT_HANDOFF_ERROR,
    protocol: WHATSHOT_HANDOFF_PROTOCOL,
    code: payload.code,
    message: payload.message,
    requestId: payload.requestId,
  } as const
}

export function validateWhatshotHandoffPayload(value: unknown): ValidatePayloadResult {
  if (!isRecord(value)) return { ok: false, error: 'Payload must be an object' }
  if (
    value.type !== WHATSHOT_HANDOFF_PAYLOAD ||
    value.protocol !== WHATSHOT_HANDOFF_PROTOCOL
  ) {
    return { ok: false, error: 'Unexpected handoff protocol' }
  }

  const requestId = isNonEmptyString(value.requestId) ? value.requestId : undefined
  if (!requestId) return { ok: false, error: 'requestId is required' }
  if (!isNonEmptyString(value.transferId)) {
    return { ok: false, error: 'transferId is required', requestId }
  }
  if (
    !Number.isSafeInteger(value.totalProductCount) ||
    Number(value.totalProductCount) < 1
  ) {
    return { ok: false, error: 'totalProductCount is invalid', requestId }
  }
  if (
    !Array.isArray(value.groups) ||
    value.groups.length < 1 ||
    value.groups.length > WHATSHOT_MAX_PRODUCTS
  ) {
    return {
      ok: false,
      error: `groups must contain 1 to ${WHATSHOT_MAX_PRODUCTS} products`,
      requestId,
    }
  }
  if (Number(value.totalProductCount) < value.groups.length) {
    return { ok: false, error: 'totalProductCount cannot be smaller than groups', requestId }
  }

  let totalBytes = 0
  const groups: WhatshotFrameGroup[] = []
  for (const valueGroup of value.groups) {
    if (!isRecord(valueGroup) || !isNonEmptyString(valueGroup.label)) {
      return { ok: false, error: 'Every group requires a label', requestId }
    }
    if (
      !Array.isArray(valueGroup.frames) ||
      valueGroup.frames.length < 1 ||
      valueGroup.frames.length > WHATSHOT_MAX_FRAMES_PER_PRODUCT
    ) {
      return {
        ok: false,
        error: `Every group must contain 1 to ${WHATSHOT_MAX_FRAMES_PER_PRODUCT} frames`,
        requestId,
      }
    }
    if (!valueGroup.frames.every(isAllowedBlob)) {
      return { ok: false, error: 'A group contains invalid image data', requestId }
    }
    const start = valueGroup.start === undefined ? undefined : validateTimestamp(valueGroup.start)
    const end = valueGroup.end === undefined ? undefined : validateTimestamp(valueGroup.end)
    if (
      (valueGroup.start !== undefined && start === null) ||
      (valueGroup.end !== undefined && end === null) ||
      (start !== undefined && end !== undefined && end < start)
    ) {
      return { ok: false, error: 'A group contains invalid video timestamps', requestId }
    }
    totalBytes += valueGroup.frames.reduce((sum, frame) => sum + frame.size, 0)
    if (totalBytes > WHATSHOT_MAX_BATCH_BYTES) {
      return { ok: false, error: 'Transfer exceeds the total size limit', requestId }
    }
    groups.push({
      label: valueGroup.label.trim().slice(0, 100),
      frames: valueGroup.frames as Blob[],
      start,
      end,
    })
  }

  let transcript: WhatshotTranscript | undefined
  if (value.transcript !== undefined) {
    transcript = validateTranscript(value.transcript)
    if (!transcript) return { ok: false, error: 'Transcript is invalid', requestId }
  }

  return {
    ok: true,
    payload: {
      requestId,
      transferId: value.transferId,
      groups,
      totalProductCount: Number(value.totalProductCount),
      transcript,
    },
  }
}
